import type { Content, Editor } from '@tiptap/core'
import {
  MessageType,
  WebSocketStatus,
  type onCloseParameters,
  type TiptapCollabProvider,
  type onOutgoingMessageParameters,
  type onUnsyncedChangesParameters,
} from '@tiptap-pro/provider'
import { throttle } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Doc, Transaction } from 'yjs'

type UseContentSyncStatusProps = Readonly<{
  editor?: Editor | null
  provider?: TiptapCollabProvider
  /**
   * Initial content to set in the editor. Only used if the remote document is empty.
   */
  initialValue?: Content
  /**
   * A callback invoked after changes are synced to the collaboration server.
   */
  onUpdateComplete?: () => void
}>

type UseContentSyncStatusValue = Readonly<{
  /**
   * The current status of the WebSocket connection to the collaboration service.
   */
  connectionStatus: WebSocketStatus | null
  /**
   * Indicates if the document has been changed since the editor was initialized.
   */
  hasChanged: boolean
  /**
   * True when the document has unversioned/draft changes.
   */
  hasDraft: boolean
  /**
   * True when the document has changes that have not been acknowledged
   * by the collaboration service.
   */
  hasUnsyncedChanges: boolean
  /**
   * Count of changes that have not been acknowledged by the collaboration service.
   */
  unsyncedChanges: number
}>

/**
 * @see https://docs.yjs.dev/api/y.doc#event-handler
 */
type YUpdateHandler = (
  update: Uint8Array,
  origin: unknown,
  doc: Doc,
  tr: Transaction
) => void

/** The server creates a version every 30 seconds unless told otherwise. */
const AUTOVERSION_INTERVAL_SEC = 60 * 10

export const useContentSyncStatus = ({
  editor,
  provider,
  initialValue,
  onUpdateComplete,
}: UseContentSyncStatusProps): UseContentSyncStatusValue => {
  const [connectionStatus, setConnectionStatus] =
    useState<WebSocketStatus | null>(null)
  const [hasChanged, setHasChanged] = useState(false)
  const [hasDraft, setHasDraft] = useState(false)
  const [unsyncedChanges, setUnsyncedChanges] = useState(0)
  const isUpdating = useRef<boolean>(false)

  // Invoked when the Y.js document updates (can be from local or remote changes)
  const onDocUpdate = useCallback<YUpdateHandler>(
    (update, origin, doc, tr) => {
      if (provider === undefined) {
        return
      }

      const collabConfigKey =
        provider.tiptapCollabConfigurationPrefix + 'config'

      if (!provider.isAutoVersioning()) {
        provider.enableAutoVersioning()
      }

      // Get changeset for the TiptapCollab config map
      const collabConfigChangeset = tr.changed.get(doc.get(collabConfigKey))
      const isVersionUpdate =
        collabConfigChangeset !== undefined &&
        collabConfigChangeset.has('currentVersion')

      // Reset the draft state to false when a version is created
      // All other updates should set the draft state to true
      setHasDraft(!isVersionUpdate)

      // Invoke onUpdateComplete callback if provided, and reset state
      if (isUpdating.current) {
        onUpdateComplete?.()
        isUpdating.current = false
      }
    },
    [provider, onUpdateComplete]
  )

  // Watches for document updates
  useEffect(() => {
    if (provider === undefined) {
      return
    }

    const { isSynced, document: doc } = provider

    const onSynced = () => {
      provider.setAutoVersioningInterval(AUTOVERSION_INTERVAL_SEC)
      // On first update only, mark hasChanged = true
      doc.once('update', () => setHasChanged(true))
      doc.on('update', onDocUpdate)
    }

    const onOutgoingMessage = ({ message }: onOutgoingMessageParameters) => {
      // MessageType.Sync occurs when document changes are synced
      if (message.type === MessageType.Sync) {
        isUpdating.current = true
      }
    }

    // If initial sync already occurred, immediately enable doc update handler
    // Otherwise, wait for sync
    if (isSynced) {
      onSynced()
    } else {
      provider.on('synced', onSynced)
    }

    provider.on('outgoingMessage', onOutgoingMessage)

    return () => {
      doc.off('update', onDocUpdate)
      provider.off('synced', onSynced)
      provider.off('outgoingMessage', onOutgoingMessage)
    }
  }, [provider, onDocUpdate])

  // Watches the provider's connection status, and
  // initiates reauthorization if needed
  useEffect(() => {
    if (provider === undefined) {
      return
    }

    const documentId = provider.configuration.name

    const onOpen = () => onStatusChange(WebSocketStatus.Connecting)
    const onDisconnect = () => onStatusChange(WebSocketStatus.Disconnected)
    const onStatusChange = (status: WebSocketStatus) => {
      setConnectionStatus((prevStatus) => {
        // Log debug message only if status changed
        if (prevStatus !== status) {
          console.debug(
            `[CollaborationTextEditor:${documentId}] Status changed to '${status}' at ${new Date().toLocaleTimeString()}`
          )
        }

        return status
      })
    }
    const onAuthenticated = () => {
      console.debug(
        `[CollaborationTextEditor:${documentId}] Authenticated at ${new Date().toLocaleTimeString()}`
      )

      // For the purposes of our connection monitoring, "connected" implies "authenticated"
      onStatusChange(WebSocketStatus.Connected)
    }
    const onAuthenticationFailed = () => {
      console.warn(
        `[CollaborationTextEditor:${documentId}] Authentication failed at ${new Date().toLocaleTimeString()}`
      )
    }
    const onClose = ({ event }: onCloseParameters) => {
      const shouldReauth =
        event.code === 4401 && event.reason.includes('JWT verification failed')

      if (shouldReauth) {
        console.debug(
          `[CollaborationTextEditor:${documentId}] Token has expired, will attempt to refresh token.`
        )
      }
    }
    const onUnsyncedChanges = throttle(
      ({ number }: onUnsyncedChangesParameters) => setUnsyncedChanges(number),
      500,
      { leading: false }
    )

    provider.on('authenticated', onAuthenticated)
    provider.on('authenticationFailed', onAuthenticationFailed)
    provider.on('open', onOpen)
    provider.on('disconnect', onDisconnect)
    provider.on('close', onClose)
    provider.on('unsyncedChanges', onUnsyncedChanges)

    return () => {
      provider.off('authenticated', onAuthenticated)
      provider.off('authenticationFailed', onAuthenticationFailed)
      provider.off('open', onOpen)
      provider.off('disconnect', onDisconnect)
      provider.off('close', onClose)
      provider.off('unsyncedChanges', onUnsyncedChanges)
      onUnsyncedChanges.cancel()
    }
  }, [provider])

  // Sets initial content on initial sync, only if remote document is empty
  useEffect(() => {
    if (provider === undefined || editor == null) {
      return
    }

    const doc = provider.document
    const onInitialContentLoaded = () => {
      if (
        initialValue !== undefined &&
        doc.getMap('config').get('initialContentLoaded') !== true &&
        editor?.commands.setContent(initialValue) === true
      ) {
        doc.getMap('config').set('initialContentLoaded', true)
        provider.off('synced', onInitialContentLoaded)
      }
    }

    provider.on('synced', onInitialContentLoaded)
    return () => {
      provider.off('synced', onInitialContentLoaded)
    }
  }, [provider, editor, initialValue])

  return {
    connectionStatus,
    hasChanged,
    hasDraft,
    hasUnsyncedChanges: unsyncedChanges > 0,
    unsyncedChanges,
  }
}
